import React, {memo, useCallback, useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    AspectRatio,
    Box,
    Button,
    Flex,
    Text,
    useStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {getImageForProductTile} from '../../../utils/utils'
import PriceSection from '../../../components/product-tile/partials/price-section'
import Swatch from '../../../components/swatch-group/swatch'
import SwatchGroup from '../../../components/swatch-group'
import useProductData from '../../../hooks/use-product-data'
import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'
import {PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID} from '../../../constants'
import {remCalc} from '../../../theme/_utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {FormattedMessage} from 'react-intl'

const COLOR_NAME_TO_HEX = {
    black: '#000000',
    white: '#FFFFFF',
    grey: '#808080',
    gray: '#808080',
    silver: '#C0C0C0',
    blue: '#0000FF',
    navy: '#000080',
    red: '#FF0000',
    green: '#008000',
    brown: '#A52A2A',
    beige: '#F5F5DC',
    sand: '#C2B280',
}

const normalizeHexColor = (value) => {
    if (typeof value !== 'string') return null
    const color = value.trim()
    if (/^#([\da-fA-F]{3}|[\da-fA-F]{6})$/.test(color)) return color
    if (/^([\da-fA-F]{3}|[\da-fA-F]{6})$/.test(color)) return `#${color}`

    const lowered = color.toLowerCase()
    return COLOR_NAME_TO_HEX[lowered] || null
}

const resolveVariantHexColor = (...candidates) => {
    for (const candidate of candidates) {
        const normalized = normalizeHexColor(candidate)
        if (normalized) return normalized
    }
    return null
}

const getHexFromObject = (obj) => {
    if (!obj || typeof obj !== 'object') return null

    const preferredKeys = [
        'hexColour',
        'hexColor',
        'hexcolour',
        'hexcolor',
        'c_hexColour',
        'c_hexColor',
        'c_hexcolour',
        'c_hexcolor',
    ]

    for (const key of preferredKeys) {
        if (obj?.[key]) return obj[key]
    }

    const dynamicHexKey = Object.keys(obj).find((key) => key.toLowerCase().includes('hex'))
    return dynamicHexKey ? obj[dynamicHexKey] : null
}

const getVariantId = (variant, index) =>
    variant?.id || variant?.productId || variant?.c_productId || `variant-${index}`

const IMAGE_TILE_BOX_SHADOW =
    '0 0 0 1px var(--border-neutral-highlight, #FFF) inset, 0 1px 2px 0 var(--neutral-xs, rgba(19, 24, 58, 0.08)), 0 0 0 1px var(--neutral-xs, rgba(19, 24, 58, 0.08));'

const CompareProductTile = ({
    productsWithCompareData: tileProduct,
    product,
    productIndex,
    onVariantChange,
}) => {
    const styles = useStyleConfig('ProductComparePlp')
    const {currency} = useCurrency()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const isProductMaster = product?.productType?.master ?? product?.type?.master ?? false
    const variationGroups = product?.c_enabledVariationGroups || []
    const initialVariantRequest = useRef(null)

    const firstVariantId = variationGroups.length > 0 ? getVariantId(variationGroups[0], 0) : null
    const currentProductId = product?.productId || product?.id
    // Swatches identify variation groups, while PLP/dropdown selections can identify SKUs.
    // Prefer a direct group ID so a color change wins over any previously merged SKU data.
    const selectedVariantCandidates = [
        currentProductId,
        tileProduct?.c_subModelIdentifier,
        product?.c_subModelIdentifier,
        product?.representedProduct?.c_subModelIdentifier,
        tileProduct?.c_variants?.length === 1
            ? tileProduct.c_variants[0]?.variationValues?.subModelIdentifier
            : null,
        product?.c_variants?.length === 1
            ? product.c_variants[0]?.variationValues?.subModelIdentifier
            : null,
    ]
    const matchingVariantId = selectedVariantCandidates.find(
        (candidate) =>
            candidate &&
            variationGroups.some((variant, index) => getVariantId(variant, index) === candidate),
    )
    const shouldPreselectFirstVariant =
        isProductMaster &&
        !matchingVariantId &&
        Boolean(firstVariantId) &&
        typeof currentProductId === 'string' &&
        currentProductId.endsWith('_sp')
    const selectedVariantId =
        matchingVariantId || (shouldPreselectFirstVariant ? firstVariantId : currentProductId)
    const navigate = useNavigation()
    const {locale} = useMultiSite()
    const localeId = locale?.alias

    const displayProduct = tileProduct || product
    const localizedProductName =
        displayProduct?.c_variantIdentifier || displayProduct?.name || displayProduct?.productName
    const displayImgSrc = getImageForProductTile(displayProduct)
    const swatchGroupAriaLabel = `${displayProduct?.c_defaultName || 'Product'} variants`
    const {priceData, productUrl} = useProductData(product, PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID)
    const displayPriceData = useMemo(() => {
        const primaryProducts = product?.c_bundleProductsInfo?.flexibleBundles?.primaryProducts
        if (!primaryProducts?.length) return priceData

        // Match the PDP's starting bundle price before optional accessories are selected.
        const toCents = (value) => Math.round(Number(value || 0) * 100)
        const currentPriceCents = primaryProducts.reduce(
            (sum, primary) => sum + toCents(primary.price),
            0,
        )
        const listPriceCents = primaryProducts.reduce(
            (sum, primary) => sum + toCents(primary.standardPrice || primary.price),
            0,
        )

        return {
            ...priceData,
            currentPrice: currentPriceCents / 100,
            listPrice: listPriceCents / 100,
            isOnSale: listPriceCents > currentPriceCents,
            isASet: true,
            isRange: true,
        }
    }, [product, priceData])

    const handleAddToCart = () => {
        navigate(`/${localeId}${productUrl}`)
    }

    useEffect(() => {
        // Initial SKUs must load the same full variation-group response as a color click.
        if (!selectedVariantId || selectedVariantId === currentProductId) return
        if (typeof onVariantChange !== 'function') return

        const requestKey = `${currentProductId}:${selectedVariantId}`
        if (initialVariantRequest.current === requestKey) return
        initialVariantRequest.current = requestKey
        onVariantChange(productIndex, selectedVariantId)
    }, [selectedVariantId, currentProductId, onVariantChange, productIndex])

    const handleVariantChange = useCallback(
        (value) => {
            if (!value || typeof onVariantChange !== 'function' || variationGroups.length === 0)
                return

            if (value === selectedVariantId) return

            const nextVariant = variationGroups.find(
                (variant, index) => getVariantId(variant, index) === value,
            )

            if (!nextVariant) return

            onVariantChange(productIndex, value)
        },
        [onVariantChange, variationGroups, selectedVariantId, productIndex],
    )

    const swatchItems = useMemo(
        () =>
            variationGroups.map((variant, index) => {
                const variantId = getVariantId(variant, index)
                const swatchName =
                    variant?.colorName ||
                    variant?.c_color ||
                    variant?.name ||
                    variant?.subModelIdentifier ||
                    variantId
                const hexColor = resolveVariantHexColor(
                    getHexFromObject(variant?.swatch),
                    getHexFromObject(variant),
                    variant?.c_hexColor,
                    variant?.c_hexColour,
                    variant?.hexColor,
                )

                return {
                    variantId,
                    swatchName,
                    hexColor,
                    position: index + 1,
                }
            }),
        [variationGroups],
    )

    return (
        <Flex width="100%" maxW={remCalc(400)} mx="auto" flexDir="column">
            <Box
                data-testid="product-tile"
                position="relative"
                display="flex"
                flexDir="column"
                w="100%"
                h="100%"
                maxW={remCalc(400)}
                mx="auto"
            >
                <Flex width="100%" gap={remCalc(40)} flexDir="column">
                    <Flex
                        width="100%"
                        flexDir="column"
                        gap={{
                            base: remCalc(16),
                            sm: remCalc(16),
                            md: remCalc(20),
                            lg: remCalc(20),
                        }}
                        alignItems="center"
                        textAlign="center"
                    >
                        <Flex
                            width="100%"
                            maxW={remCalc(400)}
                            justifyContent="center"
                            background="#E7E6E4"
                            boxShadow={IMAGE_TILE_BOX_SHADOW}
                            borderRadius="large-1"
                            onClick={handleAddToCart}
                            cursor="pointer"
                        >
                            <AspectRatio {...styles.tileImage}>
                                <DynamicImage
                                    src={displayImgSrc || ''}
                                    imageProps={{
                                        alt: '',
                                        loading: 'lazy',
                                        mixBlendMode: 'multiply',
                                    }}
                                />
                            </AspectRatio>
                        </Flex>
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            width="100%"
                            flexDir="column"
                            gap={{
                                base: 'xsmall-1.base',
                                sm: 'xsmall-1.base',
                                md: 'xsmall-1.lg',
                                lg: 'xsmall-1.lg',
                            }}
                            padding={{
                                base: 'xsmall-1.base',
                                sm: 'xsmall-1.base',
                                md: 'xsmall-1.lg',
                                lg: 'xsmall-1.lg',
                            }}
                        >
                            <Flex>
                                <Text
                                    color="foreground.neutral.default"
                                    textStyle="head-large"
                                    fontWeight="light"
                                    fontFamily="heading"
                                    maxW={remCalc(315)}
                                    noOfLines={1}
                                >
                                    {localizedProductName}
                                </Text>
                            </Flex>

                            <Flex>
                                {variationGroups.length > 0 && (
                                    <Box {...styles.cardSwatchContainer}>
                                        <SwatchGroup
                                            ariaLabel={swatchGroupAriaLabel}
                                            label=""
                                            value={selectedVariantId}
                                            handleChange={handleVariantChange}
                                            notifyOnMount={false}
                                        >
                                            {swatchItems.map((swatch) => (
                                                <Swatch
                                                    key={swatch.variantId}
                                                    value={swatch.variantId}
                                                    name={swatch.swatchName}
                                                    variant="square"
                                                    size="compare"
                                                    position={swatch.position}
                                                    total={variationGroups.length}
                                                    hexColor={swatch.hexColor}
                                                    clickOnly
                                                />
                                            ))}
                                        </SwatchGroup>
                                    </Box>
                                )}
                            </Flex>

                            <Flex width="100%" justifyContent="center">
                                <PriceSection
                                    priceData={displayPriceData}
                                    currency={currency}
                                    notShoppableCountry={notShoppableCountry}
                                    isPlpCompare={true}
                                />
                            </Flex>
                            <Flex>
                                <Button
                                    variant="outline-not-rounded"
                                    size="large"
                                    onClick={handleAddToCart}
                                >
                                    <FormattedMessage
                                        id="global.shop_now"
                                        defaultMessage="Shop Now"
                                    />
                                </Button>
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
            </Box>
        </Flex>
    )
}

CompareProductTile.propTypes = {
    productsWithCompareData: PropTypes.object,
    product: PropTypes.object,
    productIndex: PropTypes.number,
    onVariantChange: PropTypes.func,
}

export default memo(CompareProductTile)
